import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { VendorService } from 'src/app/services/core/quickbook/vendors/vendor.service';
import { BillPaymentService } from 'src/app/services/core/quickbook/bill-payments/bill-payment.service';
import {
  NUMBER_OF_RECORDS,
  BILL_PAYMENT_ENTITY_STAT_CARDS,
  BILL_PAYMENT_ENTITY_AMOUNT_STAT_CARDS,
} from 'src/app/utils/common.utils';
import { sweetAlert } from 'src/app/utils/swal';
import { debounceTime, filter, Subject } from 'rxjs';
import * as moment from 'moment';

@UntilDestroy()

@Component({
  selector: 'app-bill-payments-list',
  templateUrl: './bill-payments-list.component.html',
  styleUrls: ['./bill-payments-list.component.css']
})
export class BillPaymentsListComponent implements OnInit {

  payments: any = [];
  totalPages = 0;
  totalResults = 0;
  search: any = '';
  private searchSubject = new Subject<string>();
  currentPage = 1;
  selectedVendorId = null;
  selectedPaymentStatus = '';
  dateRange: any = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
  stats: any = {};
  statusStatCards = BILL_PAYMENT_ENTITY_STAT_CARDS;
  amountStatCards = BILL_PAYMENT_ENTITY_AMOUNT_STAT_CARDS;
  vendors: any;
  selectedDropdownIndex: any;
  toggleActionButtons = false;
  numberOfRecords = NUMBER_OF_RECORDS;

  constructor(
    private vendorService: VendorService,
    private billPaymentService: BillPaymentService,
  ) { }

  ngOnInit(): void {
    this.getVendors();
    this.initializeSearch();
    this.getPayments(this.currentPage);
  }

  getPayments(page: any) {
    const queryParams: any = { limit: this.numberOfRecords, page };
    if (this.search) queryParams.search = this.search.trim();
    if (this.selectedVendorId) queryParams.vendor = this.selectedVendorId;
    if (this.selectedPaymentStatus) queryParams.paymentStatus = this.selectedPaymentStatus;
    const dates = this.dateQuery();
    if (dates.startDate) {
      queryParams.startDate = dates.startDate;
      queryParams.endDate = dates.endDate;
    }
    this.billPaymentService.getBillPayments(queryParams).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          const { data: rows = [], overview = {} } = (data?.[0] || {}) as any;
          this.payments = rows;
          this.totalPages = overview.totalPages;
          this.totalResults = overview.totalResults;
          this.stats = overview;
        }
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getPayments(page);
  }

  onVendorFilterChange() {
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  onPaymentStatusFilterChange() {
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  onDateChange(event: any) {
    if (!event?.[0] && !event?.[1]) {
      this.dateRange = null;
      this.currentPage = 1;
      this.getPayments(this.currentPage);
      return;
    }
    if (!event?.[0] || !event?.[1]) return;
    this.dateRange = event;
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  dateQuery() {
    if (!this.dateRange?.[0] || !this.dateRange?.[1]) return {};
    return {
      startDate: moment(this.dateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(this.dateRange[1]).format('YYYY-MM-DD'),
    };
  }

  getVendors() {
    this.vendorService.getVendors({ limit: 9999 }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) this.vendors = (data.results || []).filter((v: any) => v.Id);
    });
  }

  initializeSearch() {
    this.searchSubject.pipe(
      debounceTime(500),
      filter((s: any) => !s || s.length >= 3),
      untilDestroyed(this)
    ).subscribe(() => {
      this.currentPage = 1;
      this.getPayments(this.currentPage);
    });
  }

  onSearchInput() {
    this.searchSubject.next(this.search);
  }

  async onSync(payment: any, type: 'pull' | 'push') {
    const result = await sweetAlert(
      'Alert',
      type === 'pull'
        ? 'Are you sure you want to overwrite payment data with QuickBooks data?'
        : 'Are you sure you want to overwrite the QuickBooks data with payment data?',
      'warning', 'Yes', 'No'
    );
    if (!result.value) return;
    if (type === 'pull') payment.pulling = true; else payment.pushing = true;
    this.closeActionMenu(null);
    this.billPaymentService.syncBillPayment(payment.id, { isSyncFrom: type === 'pull' })
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          this.getPayments(this.currentPage);
          sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
        },
        error: (err) => {
          sweetAlert('Error', err, 'error', 'Ok');
          payment.pulling = false;
          payment.pushing = false;
        },
        complete: () => { payment.pulling = false; payment.pushing = false; },
      });
  }

  async onVoid(payment: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to void this payment?', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.billPaymentService.voidPayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.getPayments(this.currentPage);
        sweetAlert('Success', 'Payment voided', 'success', 'Ok');
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  async onDelete(payment: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to delete this payment?', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.billPaymentService.deletePayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.getPayments(this.currentPage);
        sweetAlert('Success', 'Payment deleted', 'success', 'Ok');
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  showActionButtons(idx: any) {
    this.selectedDropdownIndex = idx;
    this.toggleActionButtons = true;
  }

  closeActionMenu(_evt: any) {
    this.selectedDropdownIndex = -1;
    this.toggleActionButtons = false;
  }
}
